import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  apiAdmincompaniesPostBodyAddressLine1Min,
  apiAdminwaitingListsPostBodyAddressLine1Max,
  apiAdminwaitingListsPostBodyAddressLine2Max,
  apiAdminwaitingListsPostBodyCityMax,
  apiAdminwaitingListsPostBodyCompanyNameMax,
  apiAdminwaitingListsPostBodyCompanyUniqueCodeMax,
  apiAdminwaitingListsPostBodyEmailMax,
  apiAdminwaitingListsPostBodyFirstnameMax,
  apiAdminwaitingListsPostBodyLastnameMax,
  apiAdminwaitingListsPostBodyPointCoeffMin,
  apiAdminwaitingListsPostBodySirenMax,
  apiAdminwaitingListsPostBodyTvaMax,
  apiAdminwaitingListsPostBodyZipcodeMax
} from '@r/api/generated-zod/index.ts';
import { WaitingListWaitingListWriteModelRole } from '@r/api/generated/client.schemas.ts';

export function buildWaitingListProposeFormSchema(typologyConversionMode: boolean, t: TFunction) {
  return z
    .object({
      companyName: z
        .string()
        .max(apiAdminwaitingListsPostBodyCompanyNameMax, t('company.create.name.max')),
      companyUniqueCode: z
        .string()
        .max(apiAdminwaitingListsPostBodyCompanyUniqueCodeMax, t('company.create.unique.code.max')),
      typology: z.string(),
      tva: z
        .string()
        .max(
          apiAdminwaitingListsPostBodyTvaMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodyTvaMax })
        ),
      siren: z
        .string()
        .max(
          apiAdminwaitingListsPostBodySirenMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodySirenMax })
        ),
      addressLine1: z
        .string()
        .max(apiAdminwaitingListsPostBodyAddressLine1Max, t('company.create.address.line.1.max'))
        .refine(
          (s) => {
            const v = s.trim();
            return v.length === 0 || v.length >= apiAdmincompaniesPostBodyAddressLine1Min;
          },
          {
            message: t('form.error.char.min', {
              minChar: apiAdmincompaniesPostBodyAddressLine1Min
            })
          }
        ),
      addressLine2: z
        .string()
        .max(
          apiAdminwaitingListsPostBodyAddressLine2Max,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodyAddressLine2Max })
        ),
      zipcode: z
        .string()
        .max(apiAdminwaitingListsPostBodyZipcodeMax, t('company.create.zipcode.max')),
      city: z.string().max(apiAdminwaitingListsPostBodyCityMax, t('company.create.city.max')),
      countryAlpha3: z.union([
        z.literal(''),
        z.string().length(3, t('company.create.country.alpha.3.required'))
      ]),
      email: z
        .string()
        .max(
          apiAdminwaitingListsPostBodyEmailMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodyEmailMax })
        )
        .refine((s) => {
          const v = s.trim();
          return v.length === 0 || z.string().email().safeParse(v).success;
        }, t('user.create.email.invalid')),
      firstname: z
        .string()
        .max(
          apiAdminwaitingListsPostBodyFirstnameMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodyFirstnameMax })
        ),
      lastname: z
        .string()
        .max(
          apiAdminwaitingListsPostBodyLastnameMax,
          t('form.error.char.max', { maxChar: apiAdminwaitingListsPostBodyLastnameMax })
        ),
      role: z.union([
        z.literal(''),
        z.enum([
          WaitingListWaitingListWriteModelRole.ROLE_MEMBER,
          WaitingListWaitingListWriteModelRole.ROLE_MANAGER
        ])
      ]),
      comment: z.string(),
      pointCoeff: z
        .union([
          z.coerce
            .number({
              message: t('company.create.point.coeff.invalid'),
              invalid_type_error: t('company.create.point.coeff.invalid')
            })
            .min(apiAdminwaitingListsPostBodyPointCoeffMin, t('company.create.point.coeff.min')),
          z.undefined()
        ])
        .optional()
    })
    .refine(
      (data) => {
        if (typologyConversionMode || data.pointCoeff === undefined || data.pointCoeff === null) {
          return true;
        }
        const decimalPlaces = data.pointCoeff.toString().split('.')[1]?.length || 0;
        return decimalPlaces <= 3;
      },
      { message: t('company.create.point.coeff.decimal.max'), path: ['pointCoeff'] }
    );
}

export type WaitingListProposeFormValues = z.infer<
  ReturnType<typeof buildWaitingListProposeFormSchema>
>;
